'use client';

/**
 * Bloque "Opciones para grupos grandes" del flujo V2 (plan §9.3). Conserva el
 * flujo de combos existente (cascada nombradas → mixto → productos) y su modal
 * transaccional, PERO se alimenta de `groupOptions` que ya vinieron en la única
 * respuesta de /api/availability — sin la 2ª llamada a /api/assignment/quote.
 */
import { useState } from 'react';
import GroupOptionCard from './group-option-card';
import AssignmentPaymentModal, { type AssignmentSelection } from './assignment-payment-modal';
import type { GroupOptionDTO } from '@/lib/assignment/quote';
import type { CTAEventProps } from '@/lib/analytics/posthog';

interface Props {
  groupOptions: GroupOptionDTO[];
  arrival: string;
  departure: string;
  adults: number;
  children: number;
  ctaAttribution: CTAEventProps;
}

export default function AssignmentGroupOptionsBlock({
  groupOptions, arrival, departure, adults, children, ctaAttribution,
}: Props) {
  const [selection, setSelection] = useState<AssignmentSelection | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  if (!groupOptions || groupOptions.length === 0) return null;

  const openGroupModal = (option: GroupOptionDTO) => {
    setSelection({
      optionFingerprint: option.optionFingerprint,
      lines: option.lines.map((l) => ({
        kind: l.kind,
        ref: l.kind === 'assignment' ? (l.productSlug ?? '') : (l.apartmentId ?? ''),
        units: l.units,
        displayName: l.displayName,
        totalPerUnit: l.totalPerUnit,
      })),
      totalCop: option.totalCop,
      depositCop: option.depositCop,
      nights: option.nights,
      arrival,
      departure,
      adults,
      children,
      hasAssignmentItems: option.lines.some((l) => l.kind === 'assignment'),
    });
    setModalOpen(true);
  };

  return (
    <section aria-labelledby="assignment-groups-title" className="mt-10">
      <div className="flex flex-wrap items-center gap-3">
        <h3 id="assignment-groups-title" className="text-2xl font-bold text-gray-900">
          Opciones para grupos grandes
        </h3>
      </div>
      <p className="mt-1 max-w-3xl text-gray-600">
        Combinamos varias unidades del mismo edificio para alojar a todo tu grupo. La unidad exacta
        se confirma al procesar tu reserva.
      </p>

      <div className="mt-5 space-y-4">
        {groupOptions.map((option) => (
          <GroupOptionCard
            key={option.optionFingerprint}
            option={option}
            arrival={arrival}
            departure={departure}
            ctaAttribution={ctaAttribution}
            onReserve={openGroupModal}
          />
        ))}
      </div>

      <AssignmentPaymentModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        selection={selection}
        ctaAttribution={ctaAttribution}
      />
    </section>
  );
}
